'use client';

/**
 * React Context primitives the renderer threads through to every block.
 *
 * <p>Lives in a leaf module (no imports back into the renderer) so
 * blocks that need {@code useThemeId} / {@code useCurrentUser} /
 * {@code useLocale} don't create the circular-import problem ThemeMaker
 * hit (theme bundle imports a block file, block file reads a context,
 * context file imports renderer). See lms-portal's
 * {@code components/block-system/contexts.tsx} for the original story.
 *
 * <p>{@code 'use client'} is required: {@code createContext} is a
 * client-only React API and the RSC compiler rejects it in server
 * modules.
 */

import { createContext, useContext } from 'react';
import type { BlockResolver } from './types';

/**
 * Current user — opaque to the renderer. Hosts decide the shape of
 * their auth user (akademi-lms-v3 uses its Redux auth state shape;
 * other hosts may use something else). Auth-aware blocks read it via
 * {@link useCurrentUser} and cast to the host-known shape.
 */
export const AuthContext = createContext<unknown | null>(null);
export function useCurrentUser<T = unknown>(): T | null {
  return useContext(AuthContext) as T | null;
}

/**
 * Active locale + fallback. Populated by {@link BlockRenderer} per
 * render. Block components read this to resolve localized strings on
 * data they fetch themselves (the prop tree is already pre-localized
 * by {@link localizeTree}; this is for runtime-fetched content).
 */
export const LocaleContext = createContext<{ locale: string; fallback: string }>({
  locale: 'en',
  fallback: 'en',
});
export function useLocale() {
  return useContext(LocaleContext);
}

/**
 * Active themeId. Populated once at the top of a render so container
 * blocks ({@code Section}, {@code Columns}) can resolve children
 * through the same theme without prop drilling.
 */
export const ThemeContext = createContext<string | null>(null);
export function useThemeId(): string | null {
  return useContext(ThemeContext);
}

/**
 * Resolved tenant brand — logo (+ dark variant) and display name from the
 * active {@code ShellConfig.brand}. Populated once by
 * {@code SiteShellPresenter} and read by any block that wants to show the
 * tenant's mark (e.g. a course-card image fallback) without needing the
 * whole shell config threaded through its own props.
 */
export interface BrandInfo {
  name?: string;
  logo?: string;
  logoDark?: string;
}
export const BrandContext = createContext<BrandInfo | null>(null);
export function useBrand(): BrandInfo | null {
  return useContext(BrandContext);
}

/**
 * Enabled-plugin set for the active tenant. Populated by the host once
 * per request (after a server-side fetch via
 * {@code apps/lms/portal/src/lib/plugins.ts#getEnabledPlugins}) and
 * consulted by the shell renderers ({@code HeaderRenderer},
 * {@code FooterRenderer}, {@code SiteShellPresenter}) to hide nav
 * entries whose {@code requiredPlugin} isn't enabled.
 *
 * <p>{@code null} (the default) means "host hasn't supplied a list" —
 * {@code filterVisible} will then skip the plugin gate entirely, so
 * shells that aren't yet wired keep working unchanged.
 */
export const PluginGateContext = createContext<ReadonlySet<string> | null>(null);

/** Hook variant — returns the active set or {@code null} when none mounted. */
export function useEnabledPlugins(): ReadonlySet<string> | null {
  return useContext(PluginGateContext);
}

/**
 * Wrap subtree with the plugin set. The host should mount this near
 * the root once it has resolved the enabled plugin list for the
 * current tenant (typically inside the site shell layer).
 */
export function PluginGateProvider({
  enabledPlugins,
  children,
}: {
  enabledPlugins: ReadonlyArray<string> | ReadonlySet<string> | null;
  children: React.ReactNode;
}) {
  const value = enabledPlugins
    ? enabledPlugins instanceof Set
      ? (enabledPlugins as ReadonlySet<string>)
      : new Set(enabledPlugins as ReadonlyArray<string>)
    : null;
  return <PluginGateContext.Provider value={value}>{children}</PluginGateContext.Provider>;
}

/**
 * Host-supplied UI-string catalog for the chrome widgets.
 *
 * <p>The site-builder package is host-agnostic: it can't reach a host's
 * i18n system (the akademi-lms app uses {@code next-i18next} with
 * {@code public/static/locales/*}; another host might use something
 * else). So instead of importing locale files, the host resolves the
 * shell's static strings against its own catalog and injects the
 * result here via {@link MessagesProvider}. Widgets look a key up with
 * {@link useMessage} and fall back to their built-in default when the
 * host hasn't supplied it — so the package still renders correctly when
 * mounted standalone (no provider = every lookup uses its built-in).
 *
 * <p>Keys are namespaced by widget, e.g. {@code 'search.recentSearches'}.
 * {@code null} (the default) means "host hasn't wired it".
 */
export type ShellMessages = Record<string, string>;
export const MessagesContext = createContext<ShellMessages | null>(null);

/**
 * Resolve a single chrome string. Returns the host-supplied value for
 * {@code key} when present, otherwise {@code fallback} (the widget's
 * built-in default for the active locale).
 */
export function useMessage(key: string, fallback: string): string {
  const messages = useContext(MessagesContext);
  return messages?.[key] ?? fallback;
}

/** Access the whole host catalog (or {@code null} when unmounted).
 *  Prefer {@link useMessage} unless resolving many keys at once. */
export function useMessages(): ShellMessages | null {
  return useContext(MessagesContext);
}

/**
 * Wrap the chrome subtree with a host string catalog. Mount once around
 * the shell — typically in the host's server-side shell mount, after it
 * has resolved the strings from its own i18n for the active locale.
 * Pass {@code null} to leave every widget on its built-in defaults.
 */
export function MessagesProvider({
  messages,
  children,
}: {
  messages: ShellMessages | null;
  children: React.ReactNode;
}) {
  return <MessagesContext.Provider value={messages}>{children}</MessagesContext.Provider>;
}

/**
 * Block resolver — injected via {@link BlockResolverProvider} at the
 * root of the host's app. {@link BlockRenderer} and
 * {@link useChildrenRenderer} read it from here.
 *
 * <p>Why context instead of ThemeMaker's module-level singleton: the
 * shared package can't assume a single global registry; multiple host
 * apps in one workspace might want different block catalogs.
 */
export const BlockResolverContext = createContext<BlockResolver | null>(null);
export function useBlockResolver(): BlockResolver {
  const r = useContext(BlockResolverContext);
  if (!r) {
    throw new Error(
      '[site-builder-portal] BlockResolverProvider not mounted. Wrap your app in ' +
        '<BlockResolverProvider value={createBlockResolver({...})}> at the root.',
    );
  }
  return r;
}

/**
 * Wraps the app with a resolver instance. Mount once near the root —
 * typically in the Next.js root layout or the portal route's client
 * boundary.
 */
export function BlockResolverProvider({
  value,
  children,
}: {
  value: BlockResolver;
  children: React.ReactNode;
}) {
  return <BlockResolverContext.Provider value={value}>{children}</BlockResolverContext.Provider>;
}
